import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { workflowApi, fileApi } from '../services/api';
import './FilePreview.css';

interface FileMetadata {
  filename: string;
  role: string;
  file_id?: string;
  upload_date?: string;
  size_bytes?: number;
  row_count?: number;
  columns?: Array<{
    name: string;
    type?: string;
    format?: string;
    max_length?: number;
    decimal_places?: number;
    unique_values?: number;
  }>;
  sheets?: Array<{
    name: string;
    columns: Array<{
      name: string;
      type?: string;
      format?: string;
    }>;
    row_count?: number;
    sample_rows?: any[][];
  }>;
  sample_rows?: any[][];
}

interface FilePreviewProps {
  workflowId: string;
}

export const FilePreview: React.FC<FilePreviewProps> = ({ workflowId }) => {
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  // Start with all files expanded by default for better visibility
  const [expandedFiles, setExpandedFiles] = useState<Set<number>>(new Set());
  const [selectedSheet, setSelectedSheet] = useState<{ [key: number]: number }>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOriginalFiles();
  }, [workflowId]);

  const fetchOriginalFiles = async () => {
    try {
      setLoading(true);
      const response = await workflowApi.getOriginalFiles(workflowId);
      const metadata = response.data;
      console.log('Original files metadata:', metadata);
      
      // Handle both old and new format
      if (metadata.files && Array.isArray(metadata.files)) {
        // Filter out duplicates and incomplete entries
        const uniqueFiles = metadata.files.reduce((acc: FileMetadata[], file: FileMetadata) => {
          // Skip files that have no meaningful content (no filename and no columns)
          if (!file.filename && !file.columns && !file.sheets) {
            console.log('Skipping incomplete file entry:', file);
            return acc;
          }
          
          // Check for duplicates based on filename (if it exists)
          if (file.filename && acc.find(f => f.filename === file.filename)) {
            console.log('Skipping duplicate file:', file.filename);
            return acc;
          }
          
          acc.push(file);
          return acc;
        }, []);
        
        console.log('Filtered files:', uniqueFiles);
        setFiles(uniqueFiles);
        // Expand all files by default
        setExpandedFiles(new Set(uniqueFiles.map((_, index) => index)));
      } else if (typeof metadata === 'object') {
        // Convert old format
        const filesList: FileMetadata[] = [];
        Object.entries(metadata).forEach(([filename, data]) => {
          if (typeof data === 'object' && data !== null && filename !== 'files') {
            filesList.push({
              filename,
              role: 'Input File',
              ...(data as any)
            });
          }
        });
        setFiles(filesList);
        // Expand all files by default
        setExpandedFiles(new Set(filesList.map((_, index) => index)));
      }
    } catch (err) {
      console.error('Failed to fetch original files:', err);
      setError('Failed to load file information');
    } finally {
      setLoading(false);
    }
  };

  const toggleFileExpanded = (index: number) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedFiles(newExpanded);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const downloadOriginalFile = async (file: FileMetadata) => {
    try {
      if (!file.file_id) {
        alert('Original file is not available for download.');
        return;
      }
      
      // Use the file download API to get the original file
      const downloadUrl = fileApi.download(file.file_id);
      
      // Create a temporary link and click it
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download original file:', err);
      alert('Failed to download original file. Please try again.');
    }
  };

  const renderColumnInfo = (columns: any[]) => {
    return (
      <div className="column-info">
        <table className="column-table">
          <thead>
            <tr>
              <th>Column Name</th>
              <th>Type</th>
              <th>Format/Details</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((col, idx) => (
              <tr key={idx}>
                <td className="column-name">{col.name}</td>
                <td className="column-type">{col.type || 'text'}</td>
                <td className="column-details">
                  {col.format && `Format: ${col.format}`}
                  {col.max_length && `Max length: ${col.max_length}`}
                  {col.decimal_places !== undefined && `Decimals: ${col.decimal_places}`}
                  {col.unique_values && `${col.unique_values} unique values`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSampleData = (columns: any[], sampleRows: any[][]) => {
    if (!sampleRows || sampleRows.length === 0) return null;

    return (
      <div className="sample-data">
        <h5>Sample Data</h5>
        <div className="sample-table-wrapper">
          <table className="sample-table">
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx}>{col.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sampleRows.slice(0, 5).map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx}>
                      {cell !== null && cell !== undefined ? String(cell) : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFileContent = (file: FileMetadata, index: number) => {
    const isExpanded = expandedFiles.has(index);
    if (!isExpanded) return null;

    // Excel file with multiple sheets
    if (file.sheets && file.sheets.length > 0) {
      const activeSheet = selectedSheet[index] || 0;
      const sheet = file.sheets[activeSheet];

      return (
        <div className="file-content">
          <div className="sheet-tabs">
            {file.sheets.map((s, idx) => (
              <button
                key={idx}
                className={`sheet-tab ${idx === activeSheet ? 'active' : ''}`}
                onClick={() => setSelectedSheet({ ...selectedSheet, [index]: idx })}
              >
                {s.name}
              </button>
            ))}
          </div>
          
          {renderColumnInfo(sheet.columns)}
          {sheet.sample_rows && renderSampleData(sheet.columns, sheet.sample_rows)}
        </div>
      );
    }

    // Single file (CSV or single-sheet Excel)
    if (file.columns) {
      return (
        <div className="file-content">
          {renderColumnInfo(file.columns)}
          {file.sample_rows && renderSampleData(file.columns, file.sample_rows)}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="file-preview-section">
        <h4>Original Input Reference</h4>
        <div className="loading-state">Loading file information...</div>
      </div>
    );
  }

  if (error || files.length === 0) {
    return (
      <div className="file-preview-section">
        <h4>Original Input Reference</h4>
        <div className="empty-state">
          {error || 'No original file information available'}
        </div>
      </div>
    );
  }

  return (
    <div className="file-preview-section">
      <h4>Original Input Reference</h4>
      <p className="section-description">
        Review the original file structure and column information. You can download the original files if needed:
      </p>
      
      {files.map((file, index) => (
        <div key={index} className="file-preview-item">
          <div className="file-header" onClick={() => toggleFileExpanded(index)}>
            <div className="file-info">
              <div className="file-icon">
                {file.filename?.endsWith('.xlsx') || file.sheets ? 
                  <FileSpreadsheet size={20} /> : 
                  <FileText size={20} />
                }
              </div>
              <div className="file-details">
                <div className="file-name">{file.filename || file.role}</div>
                <div className="file-meta">
                  {file.upload_date && `Uploaded: ${formatDate(file.upload_date)}`}
                  {file.size_bytes && ` | ${formatFileSize(file.size_bytes)}`}
                  {file.row_count && ` | ${file.row_count.toLocaleString()} rows`}
                </div>
              </div>
            </div>
            <div className="file-actions">
              <button className="expand-toggle">
                {expandedFiles.has(index) ? 
                  <ChevronDown size={16} /> : 
                  <ChevronRight size={16} />
                }
                <span>{expandedFiles.has(index) ? 'Hide Details' : 'Show Details'}</span>
              </button>
            </div>
          </div>
          
          {renderFileContent(file, index)}
          
          {expandedFiles.has(index) && file.file_id && (
            <div className="template-actions">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => downloadOriginalFile(file)}
              >
                <Download size={16} />
                <span>Download Original File</span>
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};